import { AxiosError, AxiosResponse, AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { AXIOS, API, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios } from '@umanforge/common';
import { GlobalService } from './global.service';

export class AvatarService {

  static async uploadAvatar(
    userId: UUID,
    clientId: UUID,
    file: File,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    try {
      const formData = new FormData();
      formData.append( 'content', file );
      const baseConfig: AxiosRequestConfig = await CommonGlobalService.getHeaderRequest( token );
      const baseHeaders = ( baseConfig && ( baseConfig.headers as RawAxiosRequestHeaders ) ) || {};
      const headers: RawAxiosRequestHeaders = { ...baseHeaders, 'Content-Type': 'multipart/form-data' };
      const config: AxiosRequestConfig = { ...baseConfig, headers };
      const response = await AXIOS.post( API.USERS + `${userId}/avatar/${clientId}`, formData, config );
      CommonGlobalService.saveRequestToken( response, dispatch );
      if ( response.status === 200 ) {
        return response.data && response.data.avatar ? response.data.avatar as string : null;
      }
      onAxiosWarning( response );
      return null;
    } catch ( err ) {
      onAxiosError( err as AxiosError );
      return null;
    }
  }

  static async deleteAvatar(
    userId: UUID,
    clientId: UUID,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    try {
      const baseConfig: AxiosRequestConfig = await CommonGlobalService.getHeaderRequest( token );
      // Send an empty multipart/form-data body so formidable parses correctly and backend detects NO_FILE_SELECT
      const formData = new FormData();
      const baseHeaders = ( baseConfig && ( baseConfig.headers as RawAxiosRequestHeaders ) ) || {};
      const headers: RawAxiosRequestHeaders = { ...baseHeaders, 'Content-Type': 'multipart/form-data' };
      const config: AxiosRequestConfig = { ...baseConfig, headers };
      const response = await AXIOS.post( API.USERS + `${userId}/avatar/${clientId}`, formData, config );
      CommonGlobalService.saveRequestToken( response, dispatch );
      if ( response.status === 200 ) {
        return response.data && response.data.avatar ? response.data.avatar as string : null;
      }
      onAxiosWarning( response );
      return null;
    } catch ( err ) {
      onAxiosError( err as AxiosError );
      return null;
    }
  }

  static async getUsersAvatars(
    clientId: UUID,
    usersId: UUID[],
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<{ userId: UUID, avatar: string | null }[] | null> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    try {
      const baseConfig: AxiosRequestConfig = await CommonGlobalService.getHeaderRequest( token );
      const config: AxiosRequestConfig = { ...baseConfig, params: { clientId, usersId } };
      const response = await AXIOS.get( API.USERS + 'avatars', config );
      CommonGlobalService.saveRequestToken( response, dispatch );
      if ( response.status === 200 ) {
        const raw: { userId: UUID, avatar: string | null }[] = ( response.data && response.data.avatars ) ? response.data.avatars as { userId: UUID, avatar: string | null }[] : [];
        // resolve relative avatar paths to absolute using AXIOS baseURL or window.origin
        const baseUrl = ( AXIOS && AXIOS.defaults && AXIOS.defaults.baseURL )
          ? String( AXIOS.defaults.baseURL ).replace( /\/$/, '' )
          : window.location.origin;
        return raw.map( item => {
          if ( item.avatar && item.avatar.startsWith( '/' ) ) {
            return { userId: item.userId, avatar: `${baseUrl}${item.avatar}` };
          }
          return item;
        } );
      }
      onAxiosWarning( response );
      return null;
    } catch ( err ) {
      onAxiosError( err as AxiosError );
      return null;
    }
  }

  static async getUserAvatar(
    userId: UUID,
    clientId: UUID,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    const res = await AvatarService.getUsersAvatars( clientId, [ userId ], token, dispatch, onError, onWarning );
    if ( !res || res.length === 0 ) { return null; }
    return res[ 0 ].avatar;
  }

}
